import { Link } from 'react-router-dom';
import '../styles/global.css';

export default function PrivacyPolicy() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div className="topbar">
        <Link to="/" className="topbar-logo" style={{ textDecoration: 'none' }}>
          <div className="topbar-logo-dot" />
          Affordable Telecom LLC
        </Link>
      </div>

      <div style={{ flex: 1, maxWidth: 780, margin: '0 auto', padding: '40px 20px' }}>
        <h1 style={{ fontSize: 28, fontWeight: 800, color: 'var(--navy)', marginBottom: 4 }}>Privacy Policy</h1>
        <p style={{ color: 'var(--text-sub)', fontSize: 13, marginBottom: 32 }}>
          Effective Date: April 7, 2026 &middot; Last Updated: April 7, 2026
        </p>

        <div className="card">
          <div className="card-body" style={{ fontSize: 14, lineHeight: 1.75, color: 'var(--text)' }}>

            <Section title="1. Introduction">
              <p>
                Affordable Telecom LLC ("we," "us," or "our") operates a managed internet service platform
                for residential tenants and property owners. This Privacy Policy explains how we collect, use,
                disclose, and protect your personal information when you use our website, tenant portal,
                landlord portal, and related services (collectively, the "Services").
              </p>
              <p>
                By accessing or using the Services, you agree to the terms of this Privacy Policy. If you do
                not agree, please do not use the Services.
              </p>
            </Section>

            <Section title="2. Information We Collect">
              <h4 style={h4}>2.1 Information You Provide</h4>
              <ul style={ul}>
                <li><strong>Account information:</strong> name, email address, phone number, mailing address, unit number, and password when you create a tenant or landlord account.</li>
                <li><strong>Payment information:</strong> credit/debit card numbers, bank account and routing numbers, and billing address. Payment credentials are transmitted directly to our payment processor (Stripe, Inc.) and are never stored on our servers.</li>
                <li><strong>Communications:</strong> messages you send through our support portal, email, or chat.</li>
                <li><strong>Property information:</strong> property addresses, unit counts, and ISP details provided by landlords and property managers.</li>
              </ul>

              <h4 style={h4}>2.2 Information Collected Automatically</h4>
              <ul style={ul}>
                <li><strong>Usage data:</strong> pages visited, features used, timestamps, referral URLs, and clickstream data.</li>
                <li><strong>Device data:</strong> IP address, browser type and version, operating system, and screen resolution.</li>
                <li><strong>Cookies and similar technologies:</strong> session cookies for authentication and analytics cookies to understand usage patterns. You may disable cookies in your browser settings, but some features may not function properly.</li>
              </ul>

              <h4 style={h4}>2.3 Information from Third Parties</h4>
              <ul style={ul}>
                <li>Payment status and transaction confirmations from Stripe.</li>
                <li>Address and geolocation data from OpenStreetMap / Nominatim for building search functionality.</li>
                <li>Property and tenancy information provided by your landlord or property manager.</li>
              </ul>
            </Section>

            <Section title="3. How We Use Your Information">
              <ul style={ul}>
                <li>Provide, maintain, and improve the Services, including billing, support, and account management.</li>
                <li>Process payments, generate invoices, and manage subscriptions.</li>
                <li>Send transactional emails (payment confirmations, service alerts, password resets).</li>
                <li>Send promotional communications about new plans or features, where you have opted in or where permitted by law.</li>
                <li>Generate aggregated, de-identified analytics for internal reporting and service optimization.</li>
                <li>Detect, prevent, and address fraud, abuse, and security incidents.</li>
                <li>Comply with legal obligations and respond to lawful requests from public authorities.</li>
              </ul>
            </Section>

            <Section title="4. How We Share Your Information">
              <p>We do not sell your personal information. We may share information with the following categories of recipients:</p>
              <ul style={ul}>
                <li><strong>Payment processor:</strong> Stripe, Inc. processes all payment transactions. Stripe's privacy policy governs its handling of your payment data.</li>
                <li><strong>Email service provider:</strong> SendGrid (Twilio Inc.) delivers transactional and promotional emails on our behalf.</li>
                <li><strong>Property owners/managers:</strong> Landlords may see limited tenant information (name, unit, account status) for properties they own or manage.</li>
                <li><strong>Internet service providers (ISPs):</strong> We may share your address and service tier with the underlying ISP to provision or troubleshoot your connection.</li>
                <li><strong>Legal compliance:</strong> We may disclose information when required by law, court order, subpoena, or to protect the rights, property, or safety of Affordable Telecom LLC, our users, or others.</li>
                <li><strong>Business transfers:</strong> In connection with a merger, acquisition, or sale of assets, your information may be transferred to the successor entity.</li>
              </ul>
            </Section>

            <Section title="5. Data Retention">
              <p>
                We retain your personal information for as long as your account is active or as needed to provide
                the Services. After account closure, we retain records for up to 36 months to comply with legal,
                tax, and auditing obligations. Payment transaction records are retained for 7 years as required
                by applicable financial regulations. You may request deletion of your account and associated data
                at any time by contacting us.
              </p>
            </Section>

            <Section title="6. Data Security">
              <p>
                We implement industry-standard technical and organizational measures to protect your data, including
                TLS encryption in transit, encrypted storage at rest, role-based access controls, and regular
                security assessments. Payment data is handled exclusively by Stripe and is never stored on our
                infrastructure. No method of transmission or storage is 100% secure, and we cannot guarantee
                absolute security.
              </p>
            </Section>

            <Section title="7. Your Rights — California Residents (CCPA / CPRA)">
              <p>If you are a California resident, you have the following rights under the California Consumer Privacy Act, as amended by the California Privacy Rights Act:</p>
              <ul style={ul}>
                <li><strong>Right to Know:</strong> You may request the categories and specific pieces of personal information we have collected about you.</li>
                <li><strong>Right to Delete:</strong> You may request deletion of your personal information, subject to certain legal exceptions.</li>
                <li><strong>Right to Correct:</strong> You may request correction of inaccurate personal information.</li>
                <li><strong>Right to Opt-Out of Sale/Sharing:</strong> We do not sell or share your personal information for cross-context behavioral advertising.</li>
                <li><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising any of these rights.</li>
              </ul>
              <p>
                To exercise these rights, contact us at <a href="mailto:privacy@affordable-telecom.com" style={link}>privacy@affordable-telecom.com</a>.
                We will verify your identity before processing any request and respond within 45 days.
              </p>
            </Section>

            <Section title="8. CAN-SPAM Compliance">
              <p>
                All promotional emails we send include a clear unsubscribe mechanism and our physical mailing address.
                We honor unsubscribe requests within 10 business days. Transactional emails (payment confirmations,
                security alerts, service updates) are not considered promotional and will continue to be sent as
                necessary to operate your account.
              </p>
            </Section>

            <Section title="9. TCPA Consent and Communications">
              <p>
                By providing your phone number, you consent to receive service-related calls and text messages from
                Affordable Telecom LLC, including automated or prerecorded messages, at the number you provided.
                Consent is not a condition of purchase. Message and data rates may apply. You may revoke consent at
                any time by replying STOP to any message, contacting support, or updating your communication
                preferences in your account settings.
              </p>
            </Section>

            <Section title="10. Children's Privacy">
              <p>
                The Services are not directed to individuals under the age of 18. We do not knowingly collect
                personal information from children. If we learn that we have collected information from a child
                under 18, we will promptly delete it.
              </p>
            </Section>

            <Section title="11. Changes to This Policy">
              <p>
                We may update this Privacy Policy from time to time. We will notify you of material changes by
                posting the updated policy on our website and, where appropriate, sending an email notification.
                Your continued use of the Services after any changes constitutes acceptance of the revised policy.
              </p>
            </Section>

            <Section title="12. Contact Us">
              <p>If you have questions about this Privacy Policy or wish to exercise your privacy rights, contact us at:</p>
              <div style={{ background: 'var(--light-bg)', borderRadius: 8, padding: '16px 20px', marginTop: 8 }}>
                <strong>Affordable Telecom LLC</strong><br />
                Email: <a href="mailto:privacy@affordable-telecom.com" style={link}>privacy@affordable-telecom.com</a><br />
                Web: <a href="https://www.affordable-telecom.com" style={link}>www.affordable-telecom.com</a>
              </div>
            </Section>

          </div>
        </div>

        <LegalFooter />
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', marginBottom: 10 }}>{title}</h3>
      {children}
    </div>
  );
}

function LegalFooter() {
  return (
    <div style={{ borderTop: '1px solid var(--border)', marginTop: 32, paddingTop: 20, paddingBottom: 40, textAlign: 'center', fontSize: 12, color: 'var(--text-sub)' }}>
      <p>&copy; {new Date().getFullYear()} Affordable Telecom LLC. All rights reserved.</p>
      <div style={{ marginTop: 8, display: 'flex', justifyContent: 'center', gap: 20 }}>
        <Link to="/privacy" style={link}>Privacy Policy</Link>
        <Link to="/terms" style={link}>Terms of Service</Link>
        <a href="mailto:hello@affordable-telecom.com" style={link}>Contact</a>
      </div>
    </div>
  );
}

const h4 = { fontSize: 14, fontWeight: 700, color: 'var(--navy)', margin: '16px 0 6px' };
const ul = { paddingLeft: 20, marginBottom: 12 };
const link = { color: 'var(--teal)', textDecoration: 'none', fontWeight: 600 };
